"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetTextAttributeAggregatedValues = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const graphql_1 = require("../../../../__generated__/graphql");
const ramda_1 = require("ramda");
const client_constants_1 = require("../../client.constants");
const client_utils_1 = require("../../client.utils");
const hooks_1 = require("../../hooks");
const gql_1 = require("../gql");
const values_utils_1 = require("../values.utils");
const attributes_1 = require("../../../constants/attributes");
// merge the new part with an already loaded
const updateQuery = (previousResult, options) => {
    var _a, _b;
    const { edges: prevAttributes = [] } = (_a = previousResult.productTreeNode.productAggregatedLabelsNew) !== null && _a !== void 0 ? _a : {};
    const { productTreeNode } = (_b = options === null || options === void 0 ? void 0 : options.fetchMoreResult) !== null && _b !== void 0 ? _b : {};
    if (!(productTreeNode === null || productTreeNode === void 0 ? void 0 : productTreeNode.productAggregatedLabelsNew)) {
        return previousResult;
    }
    const _c = productTreeNode.productAggregatedLabelsNew, { edges = [] } = _c, productAggregatedLabelsNew = __rest(_c, ["edges"]);
    if (!edges.length) {
        return {
            productTreeNode: Object.assign(Object.assign({}, previousResult.productTreeNode), productTreeNode),
        };
    }
    const newAttribute = edges[0].node;
    const changedAttributeIndex = prevAttributes.findIndex(({ node }) => node.label === newAttribute.label);
    const prevAttribute = prevAttributes[changedAttributeIndex];
    if (!prevAttribute) {
        return {
            productTreeNode: Object.assign(Object.assign({}, productTreeNode), { productAggregatedLabelsNew: Object.assign(Object.assign({}, productAggregatedLabelsNew), { edges: [...prevAttributes, ...edges] }) }),
        };
    }
    const changedAttribute = {
        node: Object.assign(Object.assign({}, prevAttribute.node), { values: Object.assign(Object.assign({}, newAttribute.values), { edges: [...prevAttribute.node.values.edges, ...newAttribute.values.edges] }) }),
    };
    const newAttributes = (0, ramda_1.update)(changedAttributeIndex, changedAttribute, prevAttributes);
    return {
        productTreeNode: Object.assign(Object.assign({}, productTreeNode), { productAggregatedLabelsNew: Object.assign(Object.assign({}, productAggregatedLabelsNew), { edges: newAttributes }) }),
    };
};
const fetchMoreOptions = { updateQuery };
/**
 *
 * @returns Aggregated values associated with products for a specific text attribute,
 * covering the selected node and all its children.
 *
 * Behavior controlled by the `isRequirement` filter flag:
 * - If disabled (default behavior): Only values associated with products (`productCount > 0`) are included.
 * - If enabled: Includes "In Strategy" values that are not associated with any products (`productCount = 0`),
 *   but excludes values associated with products if they are not "In Strategy."
 *   (Valid product values + rest "In Strategy" values)
 */
const useGetTextAttributeAggregatedValues = ({ catalogId, productTreeNodeId, attributeName, sortingItem, includeNA, pageSize = client_constants_1.PAGE_SIZE_LARGE, valueSearch, options, }) => {
    var _a, _b, _c, _d;
    const sort = values_utils_1.sortingItemToAggregatedValuesSortMap[sortingItem];
    const productAttributesFilter = (0, react_1.useMemo)(() => (0, values_utils_1.getAggregatedValuesProductLabelsFilter)(attributeName, { includeOnlyValidValues: true }), [attributeName]);
    const valueFilter = (0, react_1.useMemo)(() => (valueSearch
        ? { value: { contains: valueSearch, mode: graphql_1.StringFilterMode.Insensitive } }
        : undefined), [valueSearch]);
    const [getProductAggregateValues, { data, error, networkStatus, variables, fetchMore, }] = (0, react_2.useLazyQuery)(gql_1.GET_AGGREGATED_TEXT_ATTRIBUTE_VALUES, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: (_a = options === null || options === void 0 ? void 0 : options.fetchPolicy) !== null && _a !== void 0 ? _a : 'cache-first',
    });
    // using useQuery skip: true will remove all previously uploaded data
    (0, react_1.useEffect)(() => {
        if (attributeName && !(options === null || options === void 0 ? void 0 : options.skip)) {
            void getProductAggregateValues({
                variables: {
                    sort,
                    productTreeNodeId: productTreeNodeId !== null && productTreeNodeId !== void 0 ? productTreeNodeId : '',
                    catalogId,
                    filter: productAttributesFilter,
                    valueFilter,
                    includeNA,
                    first: pageSize,
                    last: null,
                    before: null,
                    after: null,
                },
            });
        }
    }, [
        productAttributesFilter,
        catalogId,
        getProductAggregateValues,
        options === null || options === void 0 ? void 0 : options.skip,
        productTreeNodeId,
        attributeName,
        sort,
        includeNA,
        pageSize,
        valueFilter,
    ]);
    const { productAggregatedLabelsNew } = (_b = data === null || data === void 0 ? void 0 : data.productTreeNode) !== null && _b !== void 0 ? _b : {};
    const selectedAttribute = (0, react_1.useMemo)(() => { var _a; return (_a = productAggregatedLabelsNew === null || productAggregatedLabelsNew === void 0 ? void 0 : productAggregatedLabelsNew.edges[0]) === null || _a === void 0 ? void 0 : _a.node; }, [productAggregatedLabelsNew]);
    const { pageInfo } = (_c = selectedAttribute === null || selectedAttribute === void 0 ? void 0 : selectedAttribute.values) !== null && _c !== void 0 ? _c : {};
    const values = (0, react_1.useMemo)(() => {
        var _a, _b, _c, _d;
        if (!data) {
            return [];
        }
        const valuesData = (_b = (_a = selectedAttribute === null || selectedAttribute === void 0 ? void 0 : selectedAttribute.values) === null || _a === void 0 ? void 0 : _a.edges) !== null && _b !== void 0 ? _b : [];
        const isAttributeModified = ((_c = selectedAttribute === null || selectedAttribute === void 0 ? void 0 : selectedAttribute.productLabelValidations) === null || _c === void 0 ? void 0 : _c.totalCount) === 0;
        const uniqueValuesMap = new Map();
        const notApplicableValue = { value: attributes_1.SpecialAttributeValue.NotApplicable, productCount: 0 };
        valuesData.forEach(({ node: { value, origin, productCount, productLabelValidations, }, }) => {
            const existingValue = value === attributes_1.SpecialAttributeValue.NotApplicable
                ? notApplicableValue
                : uniqueValuesMap.get(value);
            if (existingValue) {
                existingValue.productCount += productCount;
            }
            else {
                const isModified = (productLabelValidations === null || productLabelValidations === void 0 ? void 0 : productLabelValidations.totalCount) === 0;
                const attributeValue = {
                    value,
                    origin,
                    productCount,
                    isValid: !isModified && !isAttributeModified,
                    isModified,
                };
                uniqueValuesMap.set(value, attributeValue);
            }
        });
        const unknownValueProductCount = (_d = selectedAttribute === null || selectedAttribute === void 0 ? void 0 : selectedAttribute.unknownCount) !== null && _d !== void 0 ? _d : 0;
        const unknownValue = { value: attributes_1.SpecialAttributeValue.Unknown, productCount: unknownValueProductCount };
        const convertedValues = Array.from(uniqueValuesMap.values());
        const sortFunction = values_utils_1.sortingItemToLocalSortMap[sortingItem];
        const sortedValues = sortFunction(convertedValues, values_utils_1.sortingItemToPropName[sortingItem]);
        const pinnedSpecialValueKeys = new Set([
            attributes_1.SpecialAttributeValue.NotApplicable,
            attributes_1.SpecialAttributeValue.Unknown,
        ]);
        const valuesWithoutPinnedSpecials = sortedValues.filter(({ value }) => !pinnedSpecialValueKeys.has(String(value)));
        return [notApplicableValue, unknownValue, ...valuesWithoutPinnedSpecials];
    }, [data, selectedAttribute, sortingItem]);
    const pagination = (0, hooks_1.usePagination)({
        pageInfo,
        networkStatus,
        pageSize: client_constants_1.PAGE_SIZE_LARGE,
        fetchMore,
        fetchMoreOptions,
        lastVariables: variables,
    });
    return {
        data: values,
        pagination,
        isLoading: (0, client_utils_1.isQueryLoadingOrRefetching)(networkStatus),
        isRefetching: (0, client_utils_1.isQueryRefetching)(networkStatus),
        error,
        attributeProductCount: selectedAttribute === null || selectedAttribute === void 0 ? void 0 : selectedAttribute.productCount,
        isAttributeModified: ((_d = selectedAttribute === null || selectedAttribute === void 0 ? void 0 : selectedAttribute.productLabelValidations) === null || _d === void 0 ? void 0 : _d.totalCount) === 0,
    };
};
exports.useGetTextAttributeAggregatedValues = useGetTextAttributeAggregatedValues;
